import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";
import { axiosPath } from "../../../../routing/helpers/router";
import Translator from "../../../../layout/components/helpers/Translator";
import BreadcrumbHandler from "../../../../layout/components/breadcrumb/BreadcrumbHandler";
import errorHandler from "@/layout/components/error/ErrorHandler";
import ModuleComsaEnvironmentalSideBarReports from "../ModuleComsaEnvironmentalSideBarReports";
import ModuleComsaEnvironmentalConstructionNormalAspectsCard from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalConstruction/ModuleComsaEnvironmentalConstructionNormalAspectsCard";
import AppLink from "../../../../routing/helpers/AppLink";
import ModuleComsaEnvironmentalTitleCard from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalTitleCard";
import useIsMounted from "../../../../layout/components/hooks/useIsMounted";
import { AppNoInfo } from "@/layout/AppComponents";
import * as currentPageAction from "../../../../store/actions/currentPageAction";

export default function ModuleComsaEnvironmentalConstructionNormalAspectsScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [sheetData, setSheetData] = useState();

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            sheet: "constructionNormalAspects",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setSheetData(r.data.sheetData);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="construction"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_construction_normal_aspects" />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator
                file="module-comsaEnvironmental"
                name="construction.normal.aspects"
              />
            </Title>
            <Subtitle className="mb-4">
              <AppLink
                path="user_module_comsaEnvironmental_construction_ppi"
                params={{ moduleBridgeHex, subModuleBridgeHex, plantHex }}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="environmental.ppi"
                />
              </AppLink>
            </Subtitle>
          </div>
        </div>
        <div>
          <ModuleComsaEnvironmentalTitleCard color="warning">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="emissions.particle.emissions"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="air.pollution"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="demolitions"
            motive={
              <Translator file="module-comsaEnvironmental" name="demolitions" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="soilMovements"
            motive={
              <Translator file="module-comsaEnvironmental" name="earthworks" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="blowUp"
            motive={
              <Translator file="module-comsaEnvironmental" name="blastings" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="machineryTraffic"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="machineries.circulation"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="aridCollection"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="aggregates.stockpiles"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="ballastCollection"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="ballast.stockpiles"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="aridCrushing"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="aggregates.crushing"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="extractiveActivities"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="extractive.activities.including.quarries"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="accessOpening"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="access.opening"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="transport.process"
              />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="soilTransportation"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="transportation.soil.debris"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="transport" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="warning">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="emissions.combustion.gas.emission"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="air.pollution"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="particleBoardManufacturing"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="asphalt.production"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="concreteManufacturing"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="concrete.production"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="aridCrushing"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="crushing.aggregates"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="soilMovementMachinary"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="earthmoving.machinery"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="railwayMachinery"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="railway.machinery"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="generatorGroups"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="generator.sets"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="secondary">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="noise.vibrations.noise"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="noise.pollution"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="roadMachineryTraffic"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="track.machinery.circulation"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="soilMovementMachineryTraffic"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="earthmoving.machinery.circulation"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="demolitions2"
            motive={
              <Translator file="module-comsaEnvironmental" name="demolitions" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="blowUp2"
            motive={
              <Translator file="module-comsaEnvironmental" name="blastings" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="dig"
            motive={
              <Translator file="module-comsaEnvironmental" name="excavations" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="unformwork"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="formwork.removal"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="loanExplotaition"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="borrow.pit.exploitation"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="tablestacadoStick"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="driving.piles.sheet.piles"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="particleBoardManufacturing2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="asphalt.production"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="concreteManufacturing2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="concrete.production"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="aridCrushing2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="crushing.aggregates"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="secondary">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="noise.vibrations.vibrations"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="vibration.pollution"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="particleBoardCompaction"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="asphalt.compaction"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="soilCompaction"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="soil.compaction"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="blowUp3"
            motive={
              <Translator file="module-comsaEnvironmental" name="blastings" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="inWorkParticleBoard"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="asphalt.placement"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="inWorkConcrete"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="concrete.placement"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="tablestacadosExecution"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="execution.sheet.pile.walls"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="drivingAndScreens"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="piling.screens"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="heavyMachineryTraffic"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="heavy.machinery.circulation"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="info">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="discharges.water.discharges"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="water.soil.pollution"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="sanitaryWaters"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="sanitary.waters"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="conreteBarrelWashing"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="concrete.tank.washing"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="conreteBarrelWashing"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="tunnel.construction.discharges"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="crushingPlantsCleanning"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="crushing.plants.cleaning"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="info">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="watercourses.affections.watercourses"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="water.pollution.resource.depletion"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="coursesDiversion"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="channel.diversions"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="temporalRecruitment"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="temporary.intakes"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="gravelBedExploitation"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="gravel.quarrying"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="bridgeViaductConstruction"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="construction.bridges.viaducts"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="courseAreaSoilMovements"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="earthworks.riverbed.area"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="warning">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="morphology.soil.affections"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="soil.subsoil.alteration"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="constructionRemap"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="layout.marking.work"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="pathOpening"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="opening.roads.access.tracks"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="transport.process"
              />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="blowUp4"
            motive={
              <Translator file="module-comsaEnvironmental" name="blastings" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="gravelBedExploitation2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="exploitation.gravel.pits"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="loanExplotaition2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="exploitation.borrow.pits"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="machineryPark"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="machinery.park"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="materialsCollection"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="material.stockpiles"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="generatorGroups2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="generator.sets"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="info">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="marine.coastal.environment.bar.alteration.water.seabed.characteristics"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="affects.water.pollution.ecosystem.alteration.species.disappearance"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="dredgingsExcavations"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="dredging.excavations"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="submarineBlowUps"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="underwater.blasting"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="stuffing"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="landfills.backfills"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="soilMovements2"
            motive={
              <Translator file="module-comsaEnvironmental" name="earthworks" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="prefabricatedDrawersStuffing"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="fill.with.prefabricated.boxes"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="riprapAndPedraplen"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="breakwater.causeway"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="pilingStick"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="piling.installation"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="tablestacados"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="sheet.piling"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="drawersExecution"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="box.execution"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="drivingAndScreens2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="piling.screens"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="shipTraffic"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="vessel.dredger.diver.traffic"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="machineryTraffic4"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="machinery.circulation"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="nextToSeeAreaAridCollection"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="storage.aggregates.areas.sea"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="success">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="impact.vegetation.damage.removal.vegetation"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="construction.method"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="soilMovements3"
            motive={
              <Translator file="module-comsaEnvironmental" name="earthworks" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="aridCollection2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="aggregates.stockpiles"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="extractiveActivities2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="extractive.activities.including.quarries"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="felling"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="felling.clearing"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="remap"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="layout.marking"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="pathOpening2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="opening.roads.access.tracks"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="transport.process"
              />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="machineryTraffic2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="machinery.circulation"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="danger">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="affectation.fauna.bar.disturbance.wildlife"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="alteration.ecosystems.disappearance.animal.species"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="soilMovements4"
            motive={
              <Translator file="module-comsaEnvironmental" name="earthworks" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="machineryTraffic3"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="machinery.circulation"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="extractiveActivities3"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="extractive.activities.including.quarries"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="pathOpening3"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="road.access.opening"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="transport.process"
              />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="felling2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="felling.clearing"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="courseAreaSoilMovements2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="earthworks.riverbed.area"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="success">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="natural.resources.bar.water.consumption"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="depletion.resources"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="concreteManufacturing3"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="concrete.manufacturing"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="concreteCured"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="concrete.curing"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="watering"
            motive={
              <Translator file="module-comsaEnvironmental" name="risks" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="success">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="natural.resources.bar.fuel.consumption"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="depletion.resources"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="soilMovementMachinary2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="earthmoving.machinery"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="railwayMachinery2"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="railway.machinery"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="success">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="natural.resources.bar.electricity.consumption"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="depletion.resources"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="electricDevices"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="electrical.appliances"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="success">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="natural.resources.raw.materials.consumption"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="depletion.resources"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="constructionMaterials"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="construction.materials"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="purchases" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="secondary">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="waste.bar.urban.waste"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="visual.impact.land.occupation.water.pollution.soil.pollution"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="officeWastes"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="office.waste"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="urbanSolidWastes"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="assimilable.urban.solid.waste"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="secondary">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="waste.bar.inert.waste"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="visual.impact.land.occupation.water.pollution.soil.pollution"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="soilDebrisDemolition"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="earth.debris.from.demolition"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="constructionGeneralDebris"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="general.construction.debris.stone.ceramic.material"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="vegetalPruningRests"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="plant.pruning.residues"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="concreteCrossbeam"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="concrete.sleepers"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="ballast"
            motive={
              <Translator file="module-comsaEnvironmental" name="ballast" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="boreholeMuds"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="drilling.mud"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="secondary">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="waste.bar.non.hazardous.waste"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="visual.impact.land.occupation.water.pollution.soil.pollution"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="cardboardPaper"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="paper.cardboard"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="plastics"
            motive={
              <Translator file="module-comsaEnvironmental" name="plastics" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="woodWithWiredReel"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="wood.including.wiring.coils"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="palets"
            motive={
              <Translator file="module-comsaEnvironmental" name="pallets" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="woodCrossbeam"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="wooden.sleepers"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="constructionOriginIron"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="iron.reinforcement.from.construction"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="demolitionsOriginIron"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="iron.scrap.metal.from.demolitions.dismantling"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="tyre"
            motive={
              <Translator file="module-comsaEnvironmental" name="tires" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="secondary">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="waste.bar.hazardous.waste"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="visual.impact.land.occupation.water.pollution.soil.pollution"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="aerosols"
            motive={
              <Translator file="module-comsaEnvironmental" name="aerosols" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="oilContainersSoilMovementMachineryFilters"
            motive={ReactDOMServer.renderToString(
              <Translator
                file="module-comsaEnvironmental"
                name="oils.containers.filters.earthmoving.machinery"
              />
            )}
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="oilFatContainersRailwayMachineryFilters"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="oils.greases.containers.filters.railway.machinery"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="transformationCenterOil"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="oils.transformation.centers"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="asbestos"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="asbestos.fibrocement"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="soilMovementMachineryBatteries"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="earthmoving.machinery.batteries"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="railwayMachineryBatteries"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="railway.machinery.batteries"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="signpostingPiles"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="signaling.marking.batteries"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="signpostingPaintContainers"
            motive={ReactDOMServer.renderToString(
              <Translator
                file="module-comsaEnvironmental"
                name="paint.signaling.containers"
              />
            )}
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="waterproofPaintContainers"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="paint.finishes.waterproofing.containers"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="unformworkContainers"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="mold.release.agent.containers"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="fuelContainers"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="fuel.containers"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="ragGloveWithOil"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="rags.gloves.etc.with.oils"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="order.cleanliness"
                />{" "}
                /{" "}
                <Translator
                  file="module-comsaEnvironmental"
                  name="order.cleaning.lower.case"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="visual.impact.soil.alteration"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="innerConstructionCleaning"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="interior.cleaning.works"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="outerConstructionCleaning"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="exterior.cleaning.works"
              />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="warning">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="landscape.landscape.alteration"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="visual.impact"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="arids"
            motive={
              <Translator file="module-comsaEnvironmental" name="aggregates" />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="ballast2"
            motive={
              <Translator file="module-comsaEnvironmental" name="ballast" />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="crossbeams"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="railroad.ties"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="couples"
            motive={
              <Translator file="module-comsaEnvironmental" name="partners" />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color="primary">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="heritage.bar.historical.artistic"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="impact.on.heritage"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="soilMovements5"
            motive={
              <Translator file="module-comsaEnvironmental" name="earthworks" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="dredgings"
            motive={
              <Translator file="module-comsaEnvironmental" name="dredging" />
            }
            subphase={
              <Translator file="module-comsaEnvironmental" name="process" />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="socioeconomic.impact.bar.economic.activity"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="job.creation"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="employmentGeneration"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="job.creation"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="socioeconomic.impact.bar.economic.activity"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="increase.economic.activity"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="economicActivityIncrease"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="increase.economic.activity"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="socioeconomic.impact.bar.economic.activity"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="decrease.economic.activity"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="economicActivityDecrease"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="decrease.economic.activity"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="purchases.process"
              />
            }
          />
          <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
            <h6 className="mb-0">
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.aspect"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="socioeconomic.impact.bar.effects.on.mobility"
                />
              </span>{" "}
              /{" "}
              <span
                title={ReactDOMServer.renderToString(
                  <Translator
                    file="module-comsaEnvironmental"
                    name="environmental.impact"
                  />
                )}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="effects.mobility"
                />
              </span>
            </h6>
          </ModuleComsaEnvironmentalTitleCard>
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="wheeledTrafficDiversion"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="road.traffic.closures.diversions"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="process.transportation"
              />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="pedestrianDiversion"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="pedestrian.closures.diversions"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="process.transportation"
              />
            }
          />
          <ModuleComsaEnvironmentalConstructionNormalAspectsCard
            values={sheetData}
            name="railwayTrafficAlteration"
            motive={
              <Translator
                file="module-comsaEnvironmental"
                name="rail.traffic.closures.alterations"
              />
            }
            subphase={
              <Translator
                file="module-comsaEnvironmental"
                name="process.transportation"
              />
            }
          />
        </div>
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const Subtitle = styled.h3`
  text-align: center;
  font-size: 18px;
`;
